import { tagsApi } from "api/instances";
import { BasicTopic, Topic, TopicGroup } from "../models";
import confirm, { errorAlert } from "utils/notyPopups";
import { getDeletionProps, getInUseProps } from "../styles/props";
import { TopicTextGenerator, TopicAction, TopicItem } from "./topicTextGenerator";
import { deleteTopicsAndGroups } from "../reducer";
import { ThunkDispatch } from "@reduxjs/toolkit";

export interface TopicDeletionProps {
    topics?: BasicTopic[],
    topicGroups?: TopicGroup[];
}

export class TopicDeletionHandler {
    private topics: BasicTopic[];
    private topicGroups: TopicGroup[];

    private dispatch: ThunkDispatch<any, any, any>;

    constructor(props: TopicDeletionProps, dispatch: ThunkDispatch<any, any, any>) {
        this.topicGroups = props?.topicGroups || [];
        this.topics = props?.topics || [];
        this.dispatch = dispatch;
    }

    public onHandleDelete = async () => {
        const topicsInUse = await tagsApi.getTopicUsageStatus(this.topics.map((t: BasicTopic) => t.id));

        try {
            topicsInUse.length === 0 ?
                this.showDeletionPopup() :
                this.showInUsePopup(topicsInUse);
        } catch (error) {
            errorAlert("There has been an error processing this deletion operation");
        }
    };

    private showDeletionPopup = async () => {
        const { title, message } = this.getDeletionPopupText();
        const deleteMany = !this.isDeletingOne();

        if (await confirm.show(getDeletionProps(title, message, deleteMany))) {
            const payload = {
                topics: this.topics.map((t: BasicTopic) => t.id),
                topicGroups: this.topicGroups.map((t: TopicGroup) => t.id)
            };
            this.dispatch(deleteTopicsAndGroups(payload));
        }
    };

    private isDeletingOne = (): boolean =>
        (this.topics.length === 1 && this.topicGroups.length === 0) ||
        (this.topics.length === 0 && this.topicGroups.length === 1);

    private getDeletionPopupText = () => {
        const title = new TopicTextGenerator(TopicAction.delete);
        const message = new TopicTextGenerator(TopicAction.empty);

        const groupCount = this.topicGroups.length;
        const topicCount = this.topics.length;

        if (groupCount > 0) {
            title.add(TopicItem.topicGroup);
            message.add(`${groupCount} ${TopicItem.topicGroup}`);
            if (groupCount > 1) {
                title.plural();
                message.plural();
            }
        }

        if (topicCount > 0) {
            title.add(TopicItem.topic);
            message.add(`${topicCount} ${TopicItem.topic}`);
            if (topicCount > 1) {
                title.plural();
                message.plural();
            }
        }

        const specialMessage = groupCount === 0 && topicCount === 1 ?
            this.topics[0].name
            : null;

        return {
            message: specialMessage || message.toLowerCase().get(),
            title: title.get()
        };
    };

    private showInUsePopup = async (topicsInUse: Topic[]) => {
        await confirm.show(
            getInUseProps(topicsInUse, this.topicGroups.length >= 1)
        );
    };
}